import { ValueFormatterParams } from "ag-grid-community";
import { DateFormat, formatDate } from "common";
import { get } from "get-wild";
import { sumBy, uniq } from "lodash";
import React from "react";
import { LineItemType, Purchase, Report, Tag } from "types";
import { NoTranslate } from "../../../Common/NoTranslate";
import { MultipleArrivalDateRenderer, MultipleArrivalTimeRenderer, MultipleProductNameRenderer } from "../ColumnTypes";
import { DateRangeFilter } from "../CustomFilters/CustomDateRangeFilter";
import { CustomProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { TextFilterParams } from "../ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";

export const PurchasesReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(
        () =>
            getColumnDefs({
                report,
            }),
        [report],
    );
    return <CustomReport dateFields={["createdAt"]} report={report} columns={columns} />;
};

type GetColumnDefsParams = {
    report: Report;
};
const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "id",
        headerName: "Purchase ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "createdAt",
        headerName: "Created Date",
        type: "datetime",
        filterParams: {
            report,
        },
        dateComponent: ReportColumnFilterValueType.DATE,
        filterValueType: ReportColumnFilterValueType.DATE,
    },
    {
        field: "customerName",
        headerName: "Customer Name",
        minWidth: 200,
        cellClass: "no-translate",
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "customerEmail",
        headerName: "Customer Email",
        minWidth: 200,
        cellClass: "no-translate",
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "amount",
        headerName: "Total Value",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "balance",
        headerName: "Balance",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "items.arrivalDate",
        headerName: "Arrival Date",
        minWidth: 190,
        filter: DateRangeFilter,
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
        cellRenderer: ({ data: purchase }: { data: Purchase }) => {
            const dates: string[] = uniq<string>(get(purchase?.items, "*.arrivalDateTime")).filter((date) => !!date);
            if (dates.length === 0) {
                return "";
            }

            if (dates.length === 1) {
                return <NoTranslate>{formatDate(dates[0], DateFormat.DATE_SHORT)}</NoTranslate>;
            }

            return <MultipleArrivalDateRenderer dates={dates} />;
        },
    },
    {
        field: "items.arrivalTime",
        headerName: "Arrival Time",
        filterValueType: ReportColumnFilterValueType.TIME,
        minWidth: 190,
        cellRenderer: ({ data: purchase }: { data: Purchase }) => {
            const times: string[] = uniq(get(purchase.items, "*.arrivalTime", []));
            return times.length > 0 ? <MultipleArrivalTimeRenderer times={times} /> : null;
        },
    },
    {
        field: "items.product",
        headerName: "Experience Name",
        cellRenderer: MultipleProductNameRenderer,
        valueGetter: ({ data: purchase }: { data: Purchase }) => {
            const productIds = uniq(get(purchase.items, "*.product.id", [])).join(",");
            return productIds;
        },
        filter: CustomProductFilter,
        filterParams: {
            report,
        },
    },
    {
        field: "amountPaid",
        headerName: "Amount Paid",
        minWidth: 100,
        type: "currency",
        filter: false,
        valueGetter: ({ data }: { data: Purchase }) => {
            return sumBy(data.lineItems, (lineItem) => {
                const allowLineItemTypes = [LineItemType.PAYMENT, LineItemType.REFUND];
                return allowLineItemTypes.includes(lineItem.type) ? lineItem.amount * -1 : 0;
            });
        },
    },
    {
        field: "createdBy",
        headerName: "Created By",
        type: "user",
        minWidth: 200,
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "tags._id",
        headerName: "Tags",
        filter: "agSetColumnFilter",
        valueGetter: ({ data }) => {
            return data?.tags?.map((tag: Tag) => tag.id) || [];
        },
        filterParams: {
            valueFormatter: (params: ValueFormatterParams) => {
                return params.value;
            },
        },
    },
];
